import { ChangeEventHandler } from "react";
import { onlyNum, stringToId } from "../../../lib/funcs";
import InputGroup from "../InputGroup";
import FormGroup from "../FormGroup";
import { useElementSize } from "../../../lib/custom-hooks";
import InnerSwitcher from "./InnerSwitcher";
import { useLanguage } from "../../../lib/LanguageContext";

interface IStopLossInput {
  stopLossType: number;
  price: string | number;
  openPrice: string | number;
  onePip: string | number;
  stopLoss: string;
  setStopLossType: React.Dispatch<React.SetStateAction<number>>;
  setStopLoss: React.Dispatch<React.SetStateAction<string>>;
  setOpenPrice: React.Dispatch<React.SetStateAction<string | number>>;
  STOPLOSS_TYPES: { PIPS: number; CURRENCY: number };
}

function StopLossInput({
  stopLoss,
  setStopLoss,
  stopLossType,
  setStopLossType,
  openPrice,
  setOpenPrice,
  STOPLOSS_TYPES,
  onePip,
  price,
}: IStopLossInput) {
  const elementSize = useElementSize();
  const { languageData } = useLanguage();
  const stopLossChanged: ChangeEventHandler<HTMLInputElement> = (e) => {
    setStopLoss(onlyNum(e.target.value));
  };

  const stopLossTypeChanged = (value: string | number) => {
    const slt = Number(value);
    if (stopLossType === slt) return;
    setStopLossType(slt);
    setStopLoss((sl) => {
      if (slt === STOPLOSS_TYPES.PIPS) {
        return (
          Number(
            (Math.abs(Number(openPrice) - Number(sl)) / Number(onePip)).toFixed(
              1
            )
          ) + ""
        );
      }
      setOpenPrice(price);
      return (
        Number((Number(price) - Number(sl) * Number(onePip)).toFixed(6)) + ""
      );
    });
  };

  const openPriceChanged: ChangeEventHandler<HTMLInputElement> = (e) => {
    setOpenPrice(onlyNum(e.target.value));
  };

  let className = `col-${elementSize}`;
  if (stopLossType !== STOPLOSS_TYPES.CURRENCY)
    className = `col-${Math.min(elementSize * 2, 12)}`;

  return (
    <>
      {stopLossType === STOPLOSS_TYPES.CURRENCY ? (
        <div className={className}>
          <InputGroup
            type="text"
            label={languageData.open_price}
            value={openPrice}
            onChange={openPriceChanged}
          />
        </div>
      ) : null}
      <div className={className}>
        <FormGroup
          label={`${languageData.stop_loss} (${
            stopLossType === STOPLOSS_TYPES.PIPS
              ? languageData.pips
              : languageData.price
          })`}
        >
          <input
            id={stringToId(
              `${languageData.stop_loss} (${
                stopLossType === STOPLOSS_TYPES.PIPS
                  ? languageData.pips
                  : languageData.price
              })`
            )}
            type="text"
            onChange={stopLossChanged}
            value={stopLoss}
          />
          <InnerSwitcher
            innerSwitcher={{
              name: "sltype",
              option1: {
                isChecked: stopLossType === STOPLOSS_TYPES.PIPS,
                onClick: () => stopLossTypeChanged(STOPLOSS_TYPES.PIPS),
                text: languageData.pips,
              },
              option2: {
                isChecked: stopLossType === STOPLOSS_TYPES.CURRENCY,
                onClick: () => stopLossTypeChanged(STOPLOSS_TYPES.CURRENCY),
                text: languageData.price,
              },
            }}
          />
        </FormGroup>
      </div>
    </>
  );
}

export default StopLossInput;
